import { useState, useEffect, type ChangeEvent, type FormEvent, type MouseEvent } from "react";
import getStyles from "./styles";
import Splitter from "../Splitter";
import PDFViewer from "../PDFViewer";
import ContactInfo from "./ContactInfo";
import Instructions from "./Instructions";
import ImageCapture from "../ImageCapture";
import PrivacyPolicy from "../PrivacyPolicy";
import MainContainer from "../MainContainer";
import DirectionHandler from "../DirectionHandler";
import TermsAndConditions from "./TermsAndConditions";
import { selectText } from "../LanguageSelect/LanguageSelectSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import {
  setIsLoading,
  setIsValidated,
  selectIsValidated,
  selectIsCompleted,
  selectRequestSource,
} from "../../AppSlice";
import { setEmail, setIsSMS, selectEmail, selectIsSMS, setPhoneNumber, selectPhoneNumber } from "./InfoPageSlice";
import {
  MAIN_URL,
  AppRoutes,
  EMAIL_REGEXP,
  ReplaceParams,
  PublicEndpoints,
  MAX_EMAIL_LENGTH,
  PHONE_REGEXP_TEXT,
  PHONE_RANGE_REGEXP_TEXT,
} from "../../constants";
import { ArrowBack, Info } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";
import { Box, Button, Grid, IconButton } from "@mui/material";

export default function _InfoPage() {
  const styles = getStyles();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const isValidated = useAppSelector(selectIsValidated);
  const text = useAppSelector(selectText);
  const email = useAppSelector(selectEmail);
  const phoneNumber = useAppSelector(selectPhoneNumber);
  const isSMS = useAppSelector(selectIsSMS);
  const requestSource = useAppSelector(selectRequestSource);
  const isCompleted = useAppSelector(selectIsCompleted);

  const [isTerms, setIsTerms] = useState([false, false, false]);
  const [isPDF, setIsPDF] = useState(false);
  const [isIframe, setIsIframe] = useState(false);
  const [url, setUrl] = useState<null | string>(null);
  const [contactForm, setContactForm] = useState<"email" | "phone">(email ? "email" : phoneNumber ? "phone" : "email");
  const [errors, setErrors] = useState({
    email: false,
    phoneNumber: false,
  });
  const [dialCode, setDialCode] = useState("");
  const [isStart, setIsStart] = useState(false);

  useEffect(() => {
    if (isCompleted) {
      window.scrollTo({ top: 0, behavior: "smooth" });

      navigate(AppRoutes.completion);
    }
  }, [isCompleted]);

  const handleCheckTerms = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setIsTerms((prevState) => {
      const nextState = [...prevState];

      nextState[Number(value)] = !prevState[Number(value)];

      return nextState;
    });
  };

  const checkIsFormValid = () => {
    if (
      isTerms.filter(Boolean).length === 3 &&
      !errors.email &&
      !errors.phoneNumber &&
      ((phoneNumber && phoneNumber !== dialCode && isSMS !== null) || email)
    ) {
      scrollToBottom();

      return true;
    } else return false;
  };

  const handleOpenFile = (event: MouseEvent<HTMLButtonElement>) => {
    const name = event.currentTarget.id as "pp" | "eula";

    if (name === "eula") {
      dispatch(setIsLoading(true));

      setUrl(`${MAIN_URL}${PublicEndpoints.eulas}`.replace(ReplaceParams.language, text.language));

      setIsPDF(true);
    } else if (text.language === "german" || text.language === "french") {
      dispatch(setIsLoading(true));

      setUrl(`${MAIN_URL}${PublicEndpoints.privacyPolicies}`.replace(ReplaceParams.language, text.language));

      setIsPDF(true);
    } else {
      setUrl(text.url);

      setIsIframe(true);
    }
  };

  const handleCloseFile = () => {
    setUrl(null);
    setIsIframe(false);
    setIsPDF(false);
  };

  const handleChangeContactInfo = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setContactForm(value as "email" | "phone");
    dispatch(setEmail(null));
    dispatch(setPhoneNumber(null));
    dispatch(setIsSMS(null));
    setErrors({
      email: false,
      phoneNumber: false,
    });

    scrollToBottom();
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > MAX_EMAIL_LENGTH) return;

    if (EMAIL_REGEXP.test(value)) setErrors({ ...errors, email: false });
    else setErrors({ ...errors, email: true });

    dispatch(setEmail(value ? value : null));
  };

  const handleIsSMSChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value === "true") dispatch(setIsSMS(true));
    else dispatch(setIsSMS(false));
  };

  const handleAreaCodeChange = (
    _: ChangeEvent<{}>,
    value: { name: string; dial_code: string } | null,
    __: any,
    ___?: any
  ) => {
    if (!value) {
      setDialCode("");
      dispatch(setPhoneNumber(null));

      return;
    }

    setDialCode(value.dial_code);
    dispatch(setPhoneNumber(value.dial_code));
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!value) return dispatch(setPhoneNumber(dialCode));

    if (value.slice(0, dialCode.length) !== dialCode) return;

    if (
      new RegExp(
        PHONE_REGEXP_TEXT.replace(ReplaceParams.minimum, String(dialCode.length - 1)).replace(
          ReplaceParams.maximum,
          String(dialCode.length + 11)
        )
      ).test(value)
    )
      dispatch(setPhoneNumber(value));
    else return;

    if (
      new RegExp(
        PHONE_RANGE_REGEXP_TEXT.replace(ReplaceParams.minimum, String(dialCode.length + 7)).replace(
          ReplaceParams.maximum,
          String(dialCode.length + 11)
        )
      ).test(value)
    )
      setErrors({ ...errors, phoneNumber: false });
    else setErrors({ ...errors, phoneNumber: true });
  };

  const handleStart = (event: FormEvent) => {
    event.preventDefault();

    setIsStart(true);
  };

  const handleAbort = () => setIsStart(false);

  const scrollToBottom = () =>
    setTimeout(
      () =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        }),
      0
    );

  const handleReturn = () => dispatch(setIsValidated(false));

  if (!isValidated) return <Navigate to={AppRoutes.home} replace />;

  return (
    <Grid container component="main" justifyContent="flex-end">
      <Splitter />

      <DirectionHandler>
        <MainContainer avatar={<Info />} title={text.verificationNotice} titleVariant={"h4"}>
          {requestSource === "survivors_portal" ? (
            <></>
          ) : (
            <IconButton onClick={handleReturn} sx={styles.close}>
              <ArrowBack />
            </IconButton>
          )}

          <Instructions text={text} styles={styles} />

          <Box sx={{ width: "100%" }}>
            <ContactInfo
              contactForm={contactForm}
              text={text}
              email={email}
              errors={errors}
              isSMS={isSMS}
              phoneNumber={phoneNumber}
              dialCode={dialCode}
              styles={styles}
              handleChangeContactInfo={handleChangeContactInfo}
              handleEmailChange={handleEmailChange}
              handleAreaCodeChange={handleAreaCodeChange}
              handlePhoneChange={handlePhoneChange}
              handleIsSMSChange={handleIsSMSChange}
            />

            <TermsAndConditions
              text={text}
              isTerms={isTerms}
              styles={styles}
              handleCheckTerms={handleCheckTerms}
              handleOpenFile={handleOpenFile}
            />

            {checkIsFormValid() ? (
              <Button variant="contained" type="submit" fullWidth sx={styles.space} onClick={handleStart}>
                {text.startValidation}
              </Button>
            ) : null}
          </Box>
        </MainContainer>
      </DirectionHandler>

      <ImageCapture
        isOpen={isStart}
        text={text}
        email={email}
        phoneNumber={phoneNumber}
        isSMS={isSMS}
        close={handleAbort}
      />

      <PDFViewer close={handleCloseFile} file={url} isOpen={isPDF} />

      <PrivacyPolicy handleClose={handleCloseFile} url={url} isOpen={isIframe} />
    </Grid>
  );
}
